import type { BaseAccessCheckResult, ResourceConfig } from '@skynet/authz-core';
import {
  ANY_ID,
  createResourceChecks,
  getPermissions,
  useCerbosCheckResources,
} from '@skynet/authz-core';

import { AccountCommands, BalanceActions, BalanceCommands, BalanceHistoryCommands } from './types';
import type { BalanceActionType, BalanceResource } from './types';

interface BalancesAccessCheckProps {
  id: string;
  scope?: string;
}

type PermissionKey =
  | 'canViewBalance'
  | 'canViewBalanceHistory'
  | 'canViewBalancePrice'
  | 'canDisableAccount'
  | 'canAddBalanceCorrection'
  | 'canSetInitialBalance'
  | 'canResetInitialBalance';

interface BasePermissions extends Record<PermissionKey, boolean> {}

export interface BalancesAccessCheckResult extends BaseAccessCheckResult, BasePermissions {}

const permissionConfigs: ResourceConfig<BalanceResource, BalanceActionType>[] = [
  {
    kind: BalanceCommands.Balances,
    actionMap: {
      canViewBalance: BalanceActions.Read,
    },
  },
  {
    kind: BalanceHistoryCommands.BalanceHistory,
    actionMap: {
      canViewBalanceHistory: BalanceActions.Read,
    },
  },
  {
    kind: BalanceHistoryCommands.PriceHistory,
    actionMap: {
      canViewBalancePrice: BalanceActions.Read,
    },
  },
  {
    kind: AccountCommands.DisableAccount,
    actionMap: {
      canDisableAccount: BalanceActions.Update,
    },
  },
  {
    kind: BalanceCommands.AddBalance,
    actionMap: {
      canAddBalanceCorrection: BalanceActions.Create,
    },
  },
  {
    kind: BalanceHistoryCommands.SetInitialBalance,
    actionMap: {
      canSetInitialBalance: BalanceActions.Create,
    },
  },
  {
    kind: BalanceHistoryCommands.ResetInitialBalance,
    actionMap: {
      canResetInitialBalance: BalanceActions.Delete,
    },
  },
];

/** Reduced config used in the Trading domain scope — only balance read access is checked. */
const permissionTradingConfigs: ResourceConfig<BalanceResource, BalanceActionType>[] = [
  {
    kind: BalanceCommands.Balances,
    actionMap: {
      canViewBalance: BalanceActions.Read,
    },
  },
];

export const useBalancesAccessCheck = ({
  id,
  scope,
}: BalancesAccessCheckProps): BalancesAccessCheckResult => {
  const resources = createResourceChecks({
    configs: scope === 'Trading' ? permissionTradingConfigs : permissionConfigs,
    resourceScope: scope,
  });

  const { isLoading, error, data } = useCerbosCheckResources({ resources, id });

  return {
    ...getPermissions<BasePermissions>({ permissionConfigs, data, id: ANY_ID }),
    isLoading,
    error,
  };
};
